/**
 * Navigation menu for viewports below the desktop breakpoint.
 *
 * ## What this replaces, and why
 *
 * The previous mobile navigation was a horizontally scrolling row. Measured at
 * 375px it held **190px of links beyond the right edge** — roughly half the
 * navigation, reachable only by a swipe gesture with no affordance suggesting
 * it existed. It also cost 110px of permanent sticky chrome on a phone where
 * the whole viewport is around 640px.
 *
 * A disclosure menu costs one tap and shows everything. It also frees the room
 * to make each row a genuine 56px target instead of a 44px minimum, which
 * matters more here than saving the tap: this audience is often on a phone, in
 * a hurry, and stressed.
 *
 * ## Accessibility
 *
 * The links stay in the DOM at all times — hidden with CSS, never unmounted — so
 * crawlers and assistive technology both see the full navigation regardless of
 * open state. The button carries `aria-expanded` and `aria-controls`, Escape
 * closes and returns focus to the trigger, and a route change closes it so the
 * menu never covers the page the user just asked for.
 */

import { useEffect, useRef, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { Menu, X } from 'lucide-react'

export const MobileMenu = ({ items }: { items: { to: string; label: string }[] }) => {
  const [open, setOpen] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const { pathname } = useLocation()

  // Navigating away must close it, or the panel covers the destination.
  useEffect(() => setOpen(false), [pathname])

  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false)
        buttonRef.current?.focus()
      }
    }
    const onClickAway = (event: MouseEvent) => {
      const target = event.target as Node
      if (!panelRef.current?.contains(target) && !buttonRef.current?.contains(target)) {
        setOpen(false)
      }
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onClickAway)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('mousedown', onClickAway)
    }
  }, [open])

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        className="btn btn-ghost size-11 px-0 lg:hidden"
        aria-expanded={open}
        aria-controls="mobile-nav"
        aria-label={open ? 'Close menu' : 'Open menu'}
        onClick={() => setOpen((v) => !v)}
      >
        {open ? <X size={22} aria-hidden="true" /> : <Menu size={22} aria-hidden="true" />}
      </button>

      {/*
        `hidden` rather than conditional rendering: the links must stay in the
        document for crawlers and for assistive technology that builds its model
        from the full tree, not from what happens to be painted.
      */}
      <div
        ref={panelRef}
        id="mobile-nav"
        className={`absolute inset-x-0 top-full border-b border-base-300 bg-base-100 shadow-lg lg:hidden ${
          open ? 'block' : 'hidden'
        }`}
      >
        <nav aria-label="Main, mobile" className="anim-slide-up mx-auto max-w-7xl p-2">
          <ul>
            {items.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  className={({ isActive }) =>
                    `flex min-h-14 items-center rounded-field px-4 text-base font-medium transition-colors ${
                      isActive ? 'bg-primary/10 text-primary' : 'hover:bg-base-200'
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </>
  )
}
